import React from 'react';
import {
  Box,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  Skeleton,
} from '@mui/material';

import { useGetFacilityPermissionSchema } from '@/api/hooks/roles/useGetFacilityPermissionSchema';
import { useGetOrganizationPermissionSchema } from '@/api/hooks/roles/useGetOrganizationPermissionSchema';

const PermissionsPreviewTable = ({
  facilityPermissions,
  organizationPermissions,
}) => {
  const {
    facilityPermissionSchema,
    loading: facilityLoading,
    error: facilityError,
  } = useGetFacilityPermissionSchema();

  const {
    organizationPermissionSchema,
    loading: organizationLoading,
    error: organizationError,
  } = useGetOrganizationPermissionSchema();

  const schemaLoading = facilityLoading || organizationLoading;
  const schemaError = facilityError || organizationError;

  if (!facilityPermissions && !organizationPermissions) return null;

  const getPermissionLabel = (childKey, permission, schemaToUse) => {
    if (childKey === 'viewPage' && permission === 'read') {
      return 'Access';
    }

    for (const module of schemaToUse || []) {
      const child = module.children?.find((c) => c.key === childKey);
      if (child) {
        return `${child.title}: ${permission}`;
      }
    }

    return `${childKey}: ${permission}`;
  };

  const getModuleTitle = (moduleKey, schemaToUse) => {
    const module = schemaToUse?.find((m) => m.key === moduleKey);
    return module ? module.title : moduleKey;
  };

  const renderPermissionsSection = (
    permissions,
    title,
    schema,
    colorVariant = 'outlined',
  ) => {
    if (!permissions || Object.keys(permissions).length === 0) {
      return null;
    }

    return (
      <>
        <TableRow>
          <TableCell
            colSpan={2}
            sx={{ bgcolor: 'grey.50', fontWeight: 'bold' }}>
            {title}
          </TableCell>
        </TableRow>
        {Object.entries(permissions).map(([moduleKey, modulePermissions]) => (
          <TableRow
            key={`${title.toLowerCase().replace(' ', '-')}-${moduleKey}`}>
            <TableCell sx={{ fontWeight: 'medium', pl: 3 }}>
              {getModuleTitle(moduleKey, schema)}
            </TableCell>
            <TableCell>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {Object.entries(modulePermissions).map(([childKey, perms]) =>
                  perms.map((perm) => (
                    <Chip
                      key={`${moduleKey}-${childKey}-${perm}`}
                      label={getPermissionLabel(childKey, perm, schema)}
                      size='small'
                      color={
                        colorVariant === 'outlined' ? 'primary' : 'secondary'
                      }
                      variant={colorVariant}
                    />
                  )),
                )}
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  if (schemaLoading) {
    return (
      <Box>
        <Skeleton variant='rounded' height={200} width='100%' />
      </Box>
    );
  }

  if (schemaError) {
    return (
      <Alert severity='error' sx={{ mb: 3 }}>
        Error loading permission schema: {schemaError}
      </Alert>
    );
  }

  const hasFacilityPermissions =
    facilityPermissions && Object.keys(facilityPermissions).length > 0;
  const hasOrganizationPermissions =
    organizationPermissions && Object.keys(organizationPermissions).length > 0;

  if (!hasFacilityPermissions && !hasOrganizationPermissions) {
    return <Alert severity='info'>No permissions assigned to this role</Alert>;
  }

  return (
    <TableContainer component={Paper} variant='outlined'>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>Feature</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Permissions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {hasOrganizationPermissions &&
            renderPermissionsSection(
              organizationPermissions,
              'Organization Permissions',
              organizationPermissionSchema,
              'outlined',
            )}

          {hasFacilityPermissions &&
            renderPermissionsSection(
              facilityPermissions,
              'Facility Permissions',
              facilityPermissionSchema,
              'outlined',
            )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PermissionsPreviewTable;
